/**
 * Main Application Entry Point
 * Initializes Alpine.js, Swiper, video controllers, store locator, and mobile menu
 * Manages dynamic imports and conditional loading based on page content
 *
 * @author    Bilal Harry Lindemann
 * @link      https://lindemannrock.com
 * @copyright Copyright (c) 2025 LindemannRock
 */

import '../css/global.css';
import Alpine from 'alpinejs';

// Structured website events for GTM/GA4. The delegated listener also covers
// content replaced dynamically by Sprig.
import './analytics';

// Product filters functionality
import './filters/product-filters.js';

// Touch menu for tablets
import { initTouchMenu } from './touch-menu';

// Extend Window interface for Alpine
declare global {
	interface Window {
		Alpine: typeof Alpine;
	}
}

// Initialize Alpine.js
window.Alpine = Alpine;

// Session params store - captures URL params/hash and persists in sessionStorage
// Supports: #webview mode, UTM tracking, custom params
// TODO: Later migrate config to CMS settings
Alpine.store('session', {
	// Tracked params config (hardcoded for now, migrate to CMS later)
	_config: {
		// Webview/embed mode params
		mode: ['hideHeader', 'hideFooter'],
		// UTM tracking params
		utm: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
		// Custom params (add more as needed)
		custom: []
	},

	// State
	hideHeader: false,
	hideFooter: false,
	utm_source: null as string | null,
	utm_medium: null as string | null,
	utm_campaign: null as string | null,
	utm_term: null as string | null,
	utm_content: null as string | null,

	init() {
		const urlParams = new URLSearchParams(window.location.search);
		const hash = window.location.hash;
		const isReset = hash.includes('reset') || urlParams.has('resetSession');

		// Check for #reset to clear all webview/mode params
		if (isReset) {
			['hideHeader', 'hideFooter'].forEach((param: string) => {
				sessionStorage.removeItem(param);
			});
			this.hideHeader = false;
			this.hideFooter = false;
			// Continue to process UTM params below
		}
		// Check for #webview mode (sets both hideHeader and hideFooter)
		else if (hash.includes('webview')) {
			this.hideHeader = true;
			this.hideFooter = true;
			sessionStorage.setItem('hideHeader', 'true');
			sessionStorage.setItem('hideFooter', 'true');
		}

		// Process all tracked params
		const allParams = [
			...this._config.mode,
			...this._config.utm,
			...this._config.custom
		];

		allParams.forEach((param: string) => {
			// Skip mode params if we're resetting
			if (isReset && this._config.mode.includes(param)) {
				return;
			}

			// Check URL param first
			if (urlParams.has(param)) {
				const rawValue = urlParams.get(param);
				// Handle explicit false/0 to disable
				if (rawValue === 'false' || rawValue === '0') {
					(this as any)[param] = false;
					sessionStorage.removeItem(param);
				} else {
					const value = rawValue || 'true';
					(this as any)[param] = value === 'true' || value === '1' ? true : value;
					sessionStorage.setItem(param, String((this as any)[param]));
				}
			}
			// Fall back to sessionStorage
			else {
				const stored = sessionStorage.getItem(param);
				if (stored !== null) {
					(this as any)[param] = stored === 'true' ? true : (stored === 'false' ? false : stored);
				}
			}
		});
	},

	// Helper to get all UTM params as object (for form submission)
	getUtmParams() {
		const params: Record<string, string> = {};
		this._config.utm.forEach((key: string) => {
			const value = (this as any)[key];
			if (value) params[key] = String(value);
		});
		return params;
	},

	// Helper to get all params as query string
	toQueryString() {
		const params = new URLSearchParams();
		[...this._config.mode, ...this._config.utm, ...this._config.custom].forEach((key: string) => {
			const value = (this as any)[key];
			if (value && value !== false) {
				params.set(key, String(value));
			}
		});
		return params.toString();
	}
});

// Initialize touch menu for tablets
initTouchMenu();

/**
 * Fix: Formie phone (intl-tel-input) country button stuck disabled inside
 * conditionally-shown fields.
 *
 * When a phone field is conditionally hidden on load, Formie disables every
 * input/textarea/select/button inside it and tags them with
 * `data-conditionally-hidden-disabled` so it can re-enable them on show
 * (see Formie conditions.js). intl-tel-input then initialises on the already
 * disabled <input> and mirrors that disabled state onto the country <button>
 * it generates — but WITHOUT the marker. So when the field is shown, Formie's
 * re-enable sweep skips the button and it stays disabled forever (the dropdown
 * never opens). `disabled` is a boolean attribute, so it must be removed, not
 * set to false — which is exactly what Formie's sweep does, once the marker
 * is present.
 *
 * We listen for Formie's phone-field `init` event and copy the marker onto the
 * button, letting Formie manage it natively from then on.
 */
document.addEventListener('init', (e: Event) => {
	const detail = (e as CustomEvent).detail;
	if (!detail || !detail.phoneCountry) return; // only Formie phone-country init events

	const input = detail.phoneCountry.$field as HTMLElement | undefined;
	if (!input) return;

	const button = input.closest('.iti')?.querySelector<HTMLElement>('.iti__selected-country');
	if (!button) return;

	// iti built the button disabled because the field was conditionally hidden at
	// init — tag it so Formie's show-logic re-enables it.
	if (button.hasAttribute('disabled') && input.hasAttribute('data-conditionally-hidden-disabled')) {
		button.setAttribute('data-conditionally-hidden-disabled', 'true');
	}
});

// Check if collapse is needed and load it before starting Alpine
const hasCollapse = document.querySelector('[x-collapse]');

if (hasCollapse) {
	import('@alpinejs/collapse').then((module) => {
		Alpine.plugin(module.default);
		console.log('Alpine collapse plugin loaded');
		Alpine.start();
	});
} else {
	Alpine.start();
}

// Import Swiper for sliders
import('./swiper').then(() => {
	console.log('Swiper module loaded');

	// Import slider video controller after Swiper is loaded
	import('./slider-videos').then(({SliderVideoController, initializeVideoControllers}) => {
		console.log('Slider video controller loaded');
		// Make it globally available if needed
		(window as any).SliderVideoController = SliderVideoController;

		// Initialize video controllers after a delay to ensure Swiper is ready
		console.log('Waiting 1 second before initializing video controllers...');
		setTimeout(() => {
			console.log('Now initializing video controllers...');
			initializeVideoControllers();
		}, 1000);
	});
});

// Import store locator on store pages
if (document.querySelector('#map') || document.querySelector('#store-map')) {
	import('./store-locator').then(() => {
		console.log('Store locator module loaded');
		// Store locator auto-initializes when imported
	});
}

// Import mmenu for mobile navigation
if (document.querySelector('#mmenu-mobile')) {
	import('./mmenu').then(() => {
		console.log('Mobile menu (mmenu.js) loaded');
	});
}

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
	import.meta.hot.accept(() => {
		console.log('HMR');
	});
}
